import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Stack,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import { AiInsights } from '@features/deals/types';

interface AiValuationStepProps {
  loading: boolean;
  error: string | null;
  aiInsights: AiInsights | null;
}

const AiValuationStep: React.FC<AiValuationStepProps> = ({
  loading,
  error,
  aiInsights
}) => {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        AI Valuation Insights
      </Typography>
      {loading ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <CircularProgress />
          <Typography>Analyzing hotel data...</Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      ) : aiInsights ? (
        <Paper sx={{ p: 2, mt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Investment Risk Assessment
            </Typography>
            <Alert severity={aiInsights.investmentRisk.toLowerCase() as any} icon={false} sx={{ borderRadius: 1, mb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body1" fontWeight="bold">
                  {aiInsights.investmentRisk} Risk
                </Typography>
                <Typography variant="body2">Profitability Score: {aiInsights.profitabilityScore}/100</Typography>
              </Stack>
            </Alert>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Valuation Estimate
            </Typography>
            <Stack direction="row" spacing={2}>
              <Paper sx={{ p: 2, flex: 1, bgcolor: 'success.light' }}>
                <Typography variant="caption">Low Estimate</Typography>
                <Typography variant="h6">${aiInsights.valuationEstimate.low?.toLocaleString()}</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1, bgcolor: 'warning.light' }}>
                <Typography variant="caption">High Estimate</Typography>
                <Typography variant="h6">${aiInsights.valuationEstimate.high?.toLocaleString()}</Typography>
              </Paper>
            </Stack>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Key Performance Indicators
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableBody>
                  {Object.entries(aiInsights.keyMetrics).map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell>{key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}</TableCell>
                      <TableCell>
                        {typeof value === 'number'
                          ? key.toLowerCase().includes('rate') || key.toLowerCase().includes('margin')
                            ? `${value.toFixed(2)}%`
                            : `$${value.toLocaleString(undefined, {
                                maximumFractionDigits: 2,
                              })}`
                          : value}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Analysis Summary
            </Typography>
            <Paper sx={{ p: 2, bgcolor: 'background.paper' }}>
              <Typography variant="body1">{aiInsights.summary}</Typography>
            </Paper>
          </Box>
        </Paper>
      ) : (
        <Alert severity="info">No analysis results available</Alert>
      )}
    </Box>
  );
};

export default AiValuationStep;